import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {UnSectionedDocumentProps} from "../../../../entity/props/document/un-sectioned-document-props";
import {OrderData} from "../../../../entity/props/title/invoice-props";
import {useService} from "../../../../infrastructure/service/service";
import {PageOption} from "../../../../entity/enums/page-option";
import {router} from "../../../router/Routes";
import {ManageMode} from "../../../../entity/enums/manage-mode";
import DroppableContainer from "../document/DroppableContainer";
import TemplateDialog from "../title-template/TemplateDialog";
import InvoiceDialog from "./InvoiceDialog";
import Box from "@mui/material/Box";
import {Button, Card, CardContent, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/un-sectioned-documents.css';

const UnSectionedDocuments = (props: UnSectionedDocumentProps): JSX.Element => {
  const {projectService, titleService, titleTemplateService, titleSummaryRequestService, lineListRequestService} = useService();

  const [openTemplateDialog, setOpenTemplateDialog] = useState<boolean>(false);
  const [openInvoiceCreationDialog, setOpenInvoiceCreationDialog] = useState<boolean>(false);
  const [templateMode, setTemplateMode] = useState<ManageMode>(ManageMode.VIEW);
  const [isPermitted, setPermitted] = useState<boolean>(false);

  useEffect(() => {
    const init = async (): Promise<void> => {
      const title = titleService.getTitle();
      setPermitted(title ? projectService.isPermittedTitle(title.id) : true);

      if (titleService.getPageOption() === PageOption.EXPORT) {
        setOpenTemplateDialog(true);
        setTemplateMode(ManageMode.EXPORT)
      }

      if(titleService.getPageOption() === PageOption.DOWNLOAD) {
        setOpenTemplateDialog(true);
        setTemplateMode(ManageMode.TITLE_DOWNLOAD);
      }

      if(titleService.getPageOption() === PageOption.INVOICES) {
        setOpenInvoiceCreationDialog(true);
        setTemplateMode(ManageMode.INVOICE);
      }

      if(titleService.getPageOption() === PageOption.REPORTS && title && props.project) {
        titleSummaryRequestService.setCurrentProps({title: title, project: props.project, mode: ManageMode.CREATE});
        window.open('/title-summary-report', '_blank');
      }

      if(titleService.getPageOption() === PageOption.LINE_LIST_REPORTS && title && props.project) {
        lineListRequestService.setCurrentProps({title: title, project: props.project, mode: ManageMode.CREATE});
        window.open('/line-list-report', '_blank');
      }
    }

    init();
  }, [props.project, titleService.getPageOption(), titleTemplateService.getTitleTemplate()]);

  const handleImportFromTitleBank = async (): Promise<void> => {
    await router.navigate('/title-bank', {state: JSON.stringify(titleService.getTitle())});
  }

  const getOrderDataForInvoice = (): OrderData => {
    return {
      orderNumber: props.project?.orderNumber || '',
      propertyAddress: titleService.getTitle()?.address || '',
      parcelNumber: titleService.getTitle()?.tmsParcelNumbers[0] || '',
      clientName: props.project?.client || '',
      orderDate: props.project?.dueDate || new Date().toISOString().split('T')[0]
    };
  };

  const handleInvoiceDialogClose = (): void => {
    setOpenInvoiceCreationDialog(false);
    titleService.setPageOption(PageOption.DOCUMENTS);
  };

  return (
    <>
      <Box>
        <Box className={'un-sectioned-header-container'}>
          <Typography id={'text-unsectioned-documents-count'} className={'un-sectioned-header'}>
            Documents ({props.items && props.nonSection && props.items[props.nonSection.id].length > 0 ? props.items[props.nonSection.id].length : 0})
          </Typography>
          {
              titleService.getPageOption() === PageOption.IMPORT &&
                <Button
                    id={'btn-search-title-bank'}
                    disabled={!isPermitted}
                    disableElevation
                    className={`zitles-button icon import-button ${!isPermitted ? 'disable' : ''}`}
                    variant="contained"
                    onClick={handleImportFromTitleBank}>
                  <SearchIcon/>
                  Search on Title Bank
                </Button>
          }
        </Box>
        <Card elevation={0} className={'un-sectioned-card'}>
         <CardContent id={'un-sectioned-container'} className={'un-sectioned-card-content'}>
           <DroppableContainer
                  project={props.project}
                  items={props.items}
                  id={props.nonSection?.id ?? ''}
                  minimal
                  activeId={props.activeId}
                  key={props.nonSection?.id ?? ''}
                  isNonSection
                  setItems={props.setItems}
                  label={''}/>
         </CardContent>
       </Card>
      </Box>
      {
        openTemplateDialog &&
          <TemplateDialog
            open={openTemplateDialog}
            setOpen={setOpenTemplateDialog}
            projectTitle={titleService.getTitle()}
            mode={templateMode}
            orderNumber={props.project?.orderNumber ?? ''}/>
      }
      {
        openInvoiceCreationDialog && props.project && titleService.getTitle() &&
          <InvoiceDialog
            orderData={getOrderDataForInvoice()}
            project={props.project}
            title={titleService.getTitle()!}
            isOpen={openInvoiceCreationDialog}
            onClose={handleInvoiceDialogClose}/>
      }
    </>
  );
}

export default observer(UnSectionedDocuments);
